import { useRef } from "react";
import { useInView } from "../hooks/useInView";
import { usePrefersReducedMotion } from "../hooks/usePrefersReducedMotion";

type KineticWordmarkProps = {
  text?: string;
  accent?: string;
  /** Bazowy kolor liter - domyślnie biały; na limonkowym tle przekaż ciemny. */
  color?: string;
  className?: string;
  /** Rozmiar wordmarku. Domyślnie duży (stopka). */
  size?: "sm" | "md" | "lg";
};

/**
 * Wielki kinetyczny wordmark do stopki (styl lenis.darkroom.engineering):
 * litery wjeżdżają z dołu z obrotem 3D (rotateX -75° → 0°) ze staggerem 35 ms,
 * a na hover pojedyncza litera unosi się i wypełnia acid green.
 *
 * To "moment 3D" strony - czysty CSS transform z perspective, zero WebGL.
 *
 * Struktura litery: maska (overflow hidden) > warstwa wejścia (długi expo-out
 * ze staggerem) > warstwa hoveru (szybka, 250 ms) - dzięki temu hover nie
 * dziedziczy powolnej animacji wejścia.
 *
 * Reduced-motion: statyczny napis, bez wejścia i bez ruchu na hover
 * (kolor na hover zostaje - to wciąż czytelny stan).
 *
 * Użycie (stopka):
 *   <KineticWordmark text="POSITIVE" className="mt-24" />
 * Zastępuje statyczny FooterWatermark, jeśli oba są w konflikcie - wybierz jeden.
 */
export function KineticWordmark({ text = "POSITIVE", accent = "#BEF264", color = "rgba(255,255,255,0.95)", className, size = "lg" }: KineticWordmarkProps) {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref as React.RefObject<Element>, { once: true, margin: "0px 0px -15% 0px" });
  const reducedMotion = usePrefersReducedMotion();
  const fontSize = size === "sm" ? "clamp(32px, 6vw, 80px)" : size === "md" ? "clamp(40px, 9vw, 120px)" : "clamp(40px, 12vw, 150px)";
  const lineHeight = size === "sm" ? 0.95 : 0.9;
  // Słowa jako grupy - litery w słowie nie łamią się, zawijanie tylko MIĘDZY słowami.
  const words = text.split(" ");
  // Always gate on IO; reducedMotion only removes the stagger delay
  const entered = inView;
  // globalny indeks litery (do staggera) = suma długości wcześniejszych słów
  const wordOffsets: number[] = [];
  let acc = 0;
  for (let w = 0; w < words.length; w++) {
    wordOffsets[w] = acc;
    acc += words[w].length;
  }

  return (
    <div
      ref={ref}
      className={className}
      aria-label={text}
      style={{
        display: "flex",
        flexWrap: "wrap",
        alignItems: "flex-end",
        columnGap: "0.24em",
        rowGap: "0.02em",
        perspective: 900,
        lineHeight,
        fontFamily: "'Inter', sans-serif",
        fontWeight: 800,
        fontSize,
        letterSpacing: "-0.02em",
        color,
        userSelect: "none",
      }}
    >
      {words.map((word, wi) => (
        <span key={wi} style={{ display: "inline-flex", whiteSpace: "nowrap" }}>
          {Array.from(word).map((ch, ci) => {
            const i = wordOffsets[wi] + ci;
            return (
              // Maska - tnie literę podczas wjazdu
              <span key={i} aria-hidden="true" style={{ display: "inline-block", overflow: "hidden", verticalAlign: "top" }}>
                {/* Warstwa wejścia - wolny expo-out ze staggerem */}
                <span
                  style={{
                    display: "inline-block",
                    whiteSpace: "pre",
                    transform: entered ? "translateY(0) rotateX(0deg)" : "translateY(110%) rotateX(-75deg)",
                    transformOrigin: "50% 100%",
                    transition: reducedMotion
                      ? `transform 250ms cubic-bezier(0.16, 1, 0.3, 1)`
                      : `transform 900ms cubic-bezier(0.16, 1, 0.3, 1) ${i * 35}ms`,
                  }}
                >
                  {/* Warstwa hoveru - szybka, niezależna od wejścia */}
                  <span
                    style={{ display: "inline-block", whiteSpace: "pre", transition: "transform 250ms cubic-bezier(0.16, 1, 0.3, 1), color 250ms ease" }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.color = accent;
                      if (!reducedMotion) e.currentTarget.style.transform = "translateY(-5%)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.color = "";
                      e.currentTarget.style.transform = "translateY(0)";
                    }}
                  >
                    {ch}
                  </span>
                </span>
              </span>
            );
          })}
        </span>
      ))}
    </div>
  );
}
